/**
 * FollowupPromptDialog — the lightweight "do you want to schedule a
 * follow-up?" yes/no prompt shown after finalize/email when Claude
 * detected a vague follow-up agreement (group said they'd meet again
 * but no date was mentioned).
 *
 * This is intentionally smaller than FollowupSuggestionDialog: it's a
 * triage step so the user can dismiss in one click if they're not
 * actually planning to schedule, and only proceed to the full invite
 * form if they say yes. Specific-date detections skip this step and
 * jump straight to the form.
 */
import { Modal, ModalHeader, ModalFooter } from '../ui/Modal.tsx';

interface Props {
  isOpen: boolean;
  /** The evidence quote from Claude's detection (e.g. "let's meet again next week"). */
  evidenceQuote?: string;
  onConfirm: () => void;
  onDismiss: () => void;
}

export function FollowupPromptDialog({
  isOpen,
  evidenceQuote,
  onConfirm,
  onDismiss,
}: Props) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onDismiss}
      size="md"
      ariaLabel="Schedule follow-up?"
    >
      <ModalHeader
        title="Schedule a follow-up?"
        subtitle="A follow-up meeting came up in the discussion, but no date was given. Want to put it on the calendar now?"
        onClose={onDismiss}
      />

      {evidenceQuote && (
        <div className="border-b border-charcoal/10 bg-cloud/40 px-6 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-pine">
            Heard in the minutes
          </div>
          <div className="mt-1 text-sm italic text-charcoal/80">
            &ldquo;{evidenceQuote}&rdquo;
          </div>
        </div>
      )}

      <ModalFooter className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
        >
          Not now
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90"
        >
          Yes, schedule it
        </button>
      </ModalFooter>
    </Modal>
  );
}
